// @ts-nocheck

import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import type { ElementRef } from 'react';
import { useSpring, animated } from 'react-spring/web.cjs';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useDrag } from 'react-use-gesture';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { withFooter } from '../../../../components/ModalFooter';
import { withHeader } from '../../../../components/ModalHeader';
import ScrollView from '../../../../components/ScrollView';
import { useViewportContext } from '../../../../contexts/ViewportContext';
import usePictureInPicture from '../../../../hooks/usePictureInPicture';
import FontSize from '../../../../renderprops/FontSize';
import { ZIndices } from '../../../../styled-components/dark/common';
import { WinamaxLogo } from '../../../../styled-components/shared';
import CrossCloseButton from '../buttons/CrossCloseButton';

const ANIMATION_DURATION = 250; // ms

const scaleUp = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform:none;
    opacity: 1;
  }
`;

const scaleDown = keyframes`
  0% {
    transform: none;
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
`;

const Wrapper = styled(animated.div)`
  position: absolute;
  overflow: hidden;
  bottom: 0;
  right: 0;
  left: 0;
  font-family: BettingPoliticaExtraBold, sans-serif;

  ${({ $toTheTop, $isDark, $zIndex, theme }) => {
    const defaultStyle = css`
      background: ${$isDark ? theme.grids.gradients.black(213) : '#fff'};
      z-index: ${$zIndex || ZIndices.gridTutoModal}};
    `;
    return $toTheTop
      ? css`
          ${defaultStyle}
          top: 0;
          border-radius: 0;
        `
      : css`
          ${defaultStyle}
          top: 6em;
          top: calc(6em + constant(safe-area-inset-top));
          top: calc(6em + env(safe-area-inset-top));
          border-radius: 1em 1em 0 0;
        `;
  }}
`;

const InnerWrap = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;

  ${({ $marginTop, $fixedHeight }) => css`
    top: ${$marginTop || 0}px;
    height: calc(100% - ${$fixedHeight || 0}px);
  `}
`;

const StyledScrollView = styled(ScrollView)`
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  ${({ $centerContent }) =>
    $centerContent
      ? css`
          & > div {
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
        `
      : undefined}
`;

const WrapSlideMark = styled.div`
  height: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  position: relative;
  z-index: 1;
  padding-bottom: 1em;
`;

const SlideMark = styled.div`
  margin: 4px auto;
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.grids.colors.middlegrey3};
`;

const Header = styled.div`
  width: 100%;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const StyledTransitionGroup = styled(TransitionGroup)`
  .empty-enter .grid-cross-button {
    animation: ${scaleUp} ${ANIMATION_DURATION}ms ease-out forwards;
  }

  .empty-exit .grid-cross-button {
    animation: ${scaleDown} ${ANIMATION_DURATION}ms ease-out forwards;
  }
`;

const StyledCrossButton = styled(CrossCloseButton)`
  position: absolute;
  top: 0;
  top: constant(safe-area-inset-top);
  top: env(safe-area-inset-top);
  right: 0;
`;

const FakeNavBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(0);
  align-items: center;
  height: 3em;
  height: calc(3em + constant(safe-area-inset-top));
  height: calc(3em + env(safe-area-inset-top));
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  padding-left: constant(safe-area-inset-left);
  padding-left: env(safe-area-inset-left);
  padding-right: constant(safe-area-inset-right);
  padding-right: env(safe-area-inset-right);
  z-index: ${ZIndices.gridTutoModalNavBar};
`;

type Props = {
  children: Node;
  isDark?: boolean;
  closeModal: () => void;
  renderHeader: () => Node;
  renderFooter: () => Node;
  disableSwipe: boolean;
  isOpen: boolean;
  toTheTop?: boolean;
  centerContent?: boolean;
};

const GridModal = ({
  children,
  isDark,
  closeModal,
  renderHeader,
  renderFooter,
  isOpen = false,
  disableSwipe = false,
  toTheTop = false,
  centerContent = false,
}: Props) => {
  const { height, navbarHeight } = useViewportContext();
  const { isPipClicked } = usePictureInPicture();
  const ref = useRef<ElementRef<'div'> | null>(null);
  const refFooter = useRef<ElementRef<'div'> | null>(null);
  const refHeader = useRef<ElementRef<'div'> | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const shouldCloseSwipe = useRef<boolean>(false);

  const [{ y }, set] = useSpring(() => ({ y: isOpen ? 0 : height }));

  useEffect(() => {
    set({ y: isOpen ? 0 : height });
  }, [height, isOpen, set]);

  const bind = useDrag(
    ({ down, movement: [, my], direction: [, yDir], distance, cancel, last }) => {
      if (disableSwipe) return;
      if (!shouldCloseSwipe.current) {
        if (down && yDir > 0 && distance > height / 4) {
          shouldCloseSwipe.current = true;
        } else if (down && yDir < 0 && distance > height / 4) {
          cancel();
        }
      }
      set(() => {
        if (shouldCloseSwipe.current) {
          if (last) {
            closeModal();
            shouldCloseSwipe.current = false;
          }
          return { y: height };
        }
        return { y: down ? my : 0 };
      });
    },
    {
      useTouch: true,
    },
  );

  useLayoutEffect(() => {
    if (isOpen && refHeader.current) {
      const { height: headerHeight } = refHeader.current.getBoundingClientRect();
      setHeaderHeight(headerHeight);
    } else {
      setHeaderHeight(0);
    }
  }, [isOpen, refHeader, renderHeader]);

  useLayoutEffect(() => {
    if (isOpen && refFooter.current) {
      const { height: footerHeight } = refFooter.current.getBoundingClientRect();
      setFooterHeight(footerHeight);
    } else {
      setFooterHeight(0);
    }
  }, [height, isOpen, refFooter, renderFooter]);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      const eventListener = (event: MouseEvent) => {
        const { target } = event;
        // close the modal only when we click outside AND the pip is not clicked
        if (target instanceof Node && !node.contains(target) && !isPipClicked(target)) {
          event.preventDefault();
          event.stopPropagation();
          closeModal();
        }
      };
      if (document.body && isOpen) {
        document.body.addEventListener('click', eventListener, true);
      }
      return () => {
        if (document.body) {
          document.body.removeEventListener('click', eventListener, true);
        }
      };
    }
  }, [closeModal, isOpen, isPipClicked]);

  return (
    <FontSize>
      {({ fontSize }) => (
        <div
          style={{
            fontSize: Target.__MOBILE__ ? fontSize : 'initial',
          }}
        >
          <StyledTransitionGroup>
            {isOpen ? (
              <CSSTransition timeout={ANIMATION_DURATION} classNames="empty">
                <>
                  {toTheTop && (
                    <FakeNavBar>
                      <WinamaxLogo />
                      <StyledCrossButton onClick={closeModal} className="grid-cross-button" />
                    </FakeNavBar>
                  )}
                  <Wrapper $isDark={isDark} $toTheTop={toTheTop} ref={ref} style={{ y }}>
                    <div ref={refHeader}>
                      {!toTheTop && (
                        <WrapSlideMark {...bind()}>
                          <SlideMark />
                        </WrapSlideMark>
                      )}
                      {renderHeader && <Header>{renderHeader()}</Header>}
                    </div>
                    <InnerWrap
                      $marginTop={headerHeight + (toTheTop ? navbarHeight : 0)}
                      $fixedHeight={footerHeight + headerHeight + (toTheTop ? navbarHeight : 0)}
                    >
                      <StyledScrollView $centerContent={centerContent} disableAutoSizerHeight>
                        {children}
                      </StyledScrollView>
                    </InnerWrap>
                    {renderFooter && <Footer ref={refFooter}>{renderFooter()}</Footer>}
                  </Wrapper>
                </>
              </CSSTransition>
            ) : null}
          </StyledTransitionGroup>
        </div>
      )}
    </FontSize>
  );
};

export default withHeader(withFooter(GridModal));
